import { getDocumentStorageExtension } from '@/lib/utils';
import { supabase } from '../../../core/lib/supabase';
import type { KnowledgeBaseDocument, KnowledgeBaseWithDocuments, KnowledgeBaseFolder } from '../../../types/navigationTypes';

// Re-exportar tipos desde navigationTypes para compatibilidad
export type { KnowledgeBaseDocument, KnowledgeBaseWithDocuments };

/**
 * Obtiene las knowledge bases asociadas a una ejecución específica
 */
async function getExecutionKnowledgeBases(executionId: string): Promise<string[]> {
  try {
    console.log('🔍 Querying workpill_execution_knowledge_bases table...');
    console.log('🔍   execution_id:', executionId);
    
    const { data, error } = await supabase
      .from('workpill_execution_knowledge_bases')
      .select('knowledge_base_id')
      .eq('workpill_execution_id', executionId);

    console.log('🔍 Query result:', { data, error });

    if (error) {
      console.error('❌ Error fetching execution knowledge bases:', error);
      return [];
    }

    const kbIds = data?.map(item => item.knowledge_base_id) || [];
    console.log('🔍 Extracted KB IDs:', kbIds);
    
    return kbIds;
  } catch (error) {
    console.error('❌ Error in getExecutionKnowledgeBases:', error);
    return [];
  }
}

/**
 * Obtiene las carpetas de una knowledge base
 */
async function getFoldersForKnowledgeBase(knowledgeBaseId: string): Promise<KnowledgeBaseFolder[]> {
  try {
    const { data, error } = await supabase
      .from('nodes')
      .select('id, name, knowledge_base_id, parent_id, created_at, created_by')
      .eq('knowledge_base_id', knowledgeBaseId)
      .eq('type', 'folder');

    if (error) {
      console.error('❌ Error fetching folders:', error);
      return [];
    }

    return (data || []).map(folder => ({
      id: folder.id,
      name: folder.name,
      knowledge_base_id: folder.knowledge_base_id,
      parent_id: folder.parent_id,
      type: 'folder' as const,
      created_at: folder.created_at,
      created_by: folder.created_by
    }));
  } catch (error) {
    console.error('❌ Error in getFoldersForKnowledgeBase:', error);
    return [];
  }
}

/**
 * Obtiene los documentos de una knowledge base
 * En el nuevo modelo, los documentos son nodos de tipo 'file'
 */
async function getKnowledgeBaseDocuments(
  knowledgeBaseId: string
): Promise<KnowledgeBaseDocument[]> {
  try {
    console.log('📄 Querying nodes table for files...');
    console.log('📄   knowledge_base_id:', knowledgeBaseId);
    
    // Primero obtener las carpetas para mapear parent_id -> nombre
    const folders = await getFoldersForKnowledgeBase(knowledgeBaseId);
    const folderMap = new Map(folders.map(f => [f.id, f.name]));
    
    const { data: files, error } = await supabase
      .from('nodes')
      .select(`
        id,
        name,
        created_at,
        status,
        parent_id,
        knowledge_base_id,
        mime_type,
        size
      `)
      .eq('knowledge_base_id', knowledgeBaseId)
      .eq('type', 'file')
      .order('created_at', { ascending: false });

    console.log('📄 Query result:', { count: files?.length, error });

    if (error) {
      console.error('❌ Error fetching knowledge base files:', error);
      return [];
    }

    if (!files || files.length === 0) {
      console.log('⚠️ No files found for KB:', knowledgeBaseId);
      return [];
    }

    console.log(`📄 Found ${files.length} files, generating URLs...`);

    // Transformar los datos y generar URLs
    const transformedDocuments = await Promise.all(
      files.map(async (file: any) => {
        console.log(`📄   Processing file: ${file.name} (${file.id})`);
        
        const ext = getDocumentStorageExtension(file.mime_type);
        const storagePath = `${file.id}/document${ext}`;
        let url: string | undefined = undefined;

        try {
          const { data: urlData, error: urlError } = await supabase.storage
            .from('documents')
            .createSignedUrl(storagePath, 3600);

          if (urlError) {
            console.warn(`⚠️ Could not generate URL for file ${file.id}:`, urlError);
          } else {
            url = urlData?.signedUrl;
            console.log(`✅     URL generated successfully`);
          }
        } catch (urlError) {
          console.warn(`⚠️ Error generating URL for file ${file.id}:`, urlError);
        }

        return {
          id: file.id,
          name: file.name,
          description: undefined,
          storage_path: storagePath,
          created_at: file.created_at,
          status: file.status as 'pending' | 'processing' | 'completed' | 'failed',
          parent_id: file.parent_id,
          parent_name: file.parent_id ? folderMap.get(file.parent_id) : undefined,
          knowledge_base_id: file.knowledge_base_id,
          knowledge_base_name: '',  // Se rellenará después
          url,
          type: 'file' as const,
          mime_type: file.mime_type,
          size: file.size
        };
      })
    );

    console.log(`✅ Transformed ${transformedDocuments.length} documents`);
    return transformedDocuments;
  } catch (error) {
    console.error('❌ Error in getKnowledgeBaseDocuments:', error);
    return [];
  }
}

/**
 * Obtiene los documentos de knowledge bases para una ejecución
 * Solo usa KBs asociadas en workpill_execution_knowledge_bases
 */
export async function getKnowledgeBaseDocumentsForExecution(
  executionId: string,
  _workpillId?: string
): Promise<KnowledgeBaseWithDocuments[]> {
  try {
    console.log('📚 ========================================');
    console.log('📚 Fetching knowledge base documents...');
    console.log('📚 ========================================');
    console.log('📚 Execution ID:', executionId);
    console.log('📚 Workpill ID (unused):', _workpillId);

    // 1. Obtener KBs de la ejecución
    console.log('📚 Step 1: Checking execution knowledge bases...');
    const executionKBIds = await getExecutionKnowledgeBases(executionId);
    console.log('📚 KBs from execution:', executionKBIds);

    // 2. Usar exclusivamente las KBs de la ejecución
    const knowledgeBaseIds = Array.from(new Set(executionKBIds));
    console.log('📚 Unique execution KBs:', knowledgeBaseIds);

    if (knowledgeBaseIds.length === 0) {
      console.log('⚠️ No knowledge bases found for execution');
      return [];
    }

    // 3. Obtener información de las KBs (incluye is_global para Shared/Project)
    console.log('📚 Step 3: Fetching knowledge base details...');
    console.log('📚 Looking for KB IDs:', knowledgeBaseIds);
    
    const { data: knowledgeBases, error: kbError } = await supabase
      .from('knowledge_bases')
      .select('id, name, description, created_at, is_global, type, metadata')
      .in('id', knowledgeBaseIds);

    console.log('📚 Knowledge bases query result:', { data: knowledgeBases, error: kbError });

    if (kbError) {
      console.error('❌ Error fetching knowledge bases info:', kbError);
      return [];
    }

    if (!knowledgeBases || knowledgeBases.length === 0) {
      console.log('⚠️ No knowledge bases found in database');
      return [];
    }

    console.log(`📚 Found ${knowledgeBases.length} knowledge bases`);

    // 4. Para cada KB, obtener sus documentos
    console.log('📚 Step 4: Fetching documents for each KB...');
    const knowledgeBasesWithDocuments = await Promise.all(
      knowledgeBases.map(async (kb) => {
        console.log(`📚 Fetching documents for KB: ${kb.name} (${kb.id})`);
        const documents = await getKnowledgeBaseDocuments(kb.id);
        console.log(`📚   Found ${documents.length} documents in KB ${kb.name}`);
        
        // Determinar si es una KB asociada a la ejecución actual (para permitir subida)
        const isExecutionAssociated = executionKBIds.includes(kb.id);

        // Asignar el nombre de KB a cada documento
        const docsWithKBName = documents.map(doc => ({
          ...doc,
          knowledge_base_name: kb.name
        }));

        return {
          id: kb.id,
          name: kb.name,
          description: kb.description,
          created_at: kb.created_at,
          is_global: kb.is_global, // Usar el valor real de la base de datos
          type: kb.type,
          metadata: {
            ...kb.metadata,
            is_execution_associated: isExecutionAssociated // Guardar esto en metadata para uso UI
          },
          documents: docsWithKBName
        };
      })
    );

    const totalDocs = knowledgeBasesWithDocuments.reduce((sum, kb) => sum + kb.documents.length, 0);
    console.log('✅ ========================================');
    console.log(`✅ Found ${knowledgeBasesWithDocuments.length} knowledge bases with ${totalDocs} total documents`);
    console.log('✅ ========================================');
    
    return knowledgeBasesWithDocuments;

  } catch (error) {
    console.error('❌ Error in getKnowledgeBaseDocumentsForExecution:', error);
    return [];
  }
}

/**
 * Obtiene todos los documentos de todas las KBs asociadas (sin agrupar por KB)
 */
export async function getAllKnowledgeBaseDocuments(
  executionId: string,
  workpillId?: string
): Promise<KnowledgeBaseDocument[]> {
  const knowledgeBases = await getKnowledgeBaseDocumentsForExecution(executionId, workpillId);
  
  // Aplanar todos los documentos
  const allDocuments = knowledgeBases.flatMap(kb => kb.documents);
  
  return allDocuments;
}

/**
 * Crea una nueva Knowledge Base y la asocia a una ejecución específica
 */
export type OrgSharedKnowledgeBaseRow = {
  id: string;
  name: string;
  type: string | null;
};

/**
 * Shared (org-level) knowledge bases the user can link to an execution.
 */
export async function fetchOrganizationSharedKnowledgeBases(
  organizationId: string
): Promise<OrgSharedKnowledgeBaseRow[]> {
  try {
    const { data, error } = await supabase
      .from('knowledge_bases')
      .select('id, name, type')
      .eq('organization_id', organizationId)
      .eq('is_global', true)
      .order('name');

    if (error) {
      console.error('❌ Error fetching organization shared knowledge bases:', error);
      return [];
    }
    return data || [];
  } catch (e) {
    console.error('❌ fetchOrganizationSharedKnowledgeBases:', e);
    return [];
  }
}

/**
 * IDs of shared KBs currently linked to this execution.
 */
export async function fetchExecutionSharedKnowledgeBaseIds(executionId: string): Promise<string[]> {
  const kbIds = await getExecutionKnowledgeBases(executionId);
  if (kbIds.length === 0) return [];

  const { data: kbs, error } = await supabase
    .from('knowledge_bases')
    .select('id, is_global')
    .in('id', kbIds);

  if (error) {
    console.error('❌ Error resolving shared KB links:', error);
    return [];
  }

  return (kbs || []).filter((k) => k.is_global === true).map((k) => k.id);
}

/**
 * Replace only shared-KB links for an execution; project-scoped KB links are untouched.
 */
export async function syncExecutionSharedKnowledgeBaseLinks(
  executionId: string,
  desiredSharedKbIds: string[]
): Promise<{ error: Error | null }> {
  try {
    const currentShared = await fetchExecutionSharedKnowledgeBaseIds(executionId);
    const desired = new Set(desiredSharedKbIds);
    const current = new Set(currentShared);
    const toRemove = currentShared.filter((id) => !desired.has(id));
    const toAdd = desiredSharedKbIds.filter((id) => !current.has(id));

    for (const kbId of toRemove) {
      const { error } = await supabase
        .from('workpill_execution_knowledge_bases')
        .delete()
        .eq('workpill_execution_id', executionId)
        .eq('knowledge_base_id', kbId);
      if (error) return { error: new Error(error.message) };
    }

    if (toAdd.length > 0) {
      const { error } = await supabase.from('workpill_execution_knowledge_bases').insert(
        toAdd.map((knowledge_base_id) => ({
          workpill_execution_id: executionId,
          knowledge_base_id,
        }))
      );
      if (error) return { error: new Error(error.message) };
    }

    return { error: null };
  } catch (e) {
    return { error: e instanceof Error ? e : new Error(String(e)) };
  }
}

export async function createKnowledgeBaseForExecution(params: {
  name: string;
  description?: string;
  executionId: string;
  userId: string;
  organizationId: string;
  is_global?: boolean;
  type?: 'normal' | 'sharepoint' | 'google-drive' | 'outlook';
  metadata?: Record<string, unknown>;
}): Promise<string | null> {
  try {
    console.log('🆕 Creating knowledge base for execution:', params.executionId);

    const { data: kb, error: kbError } = await supabase
      .from('knowledge_bases')
      .insert({
        name: params.name,
        description: params.description ?? '',
        created_by: params.userId,
        organization_id: params.organizationId,
        is_global: params.is_global ?? false,
        type: params.type ?? 'normal',
        metadata: params.metadata ?? {},
      })
      .select('id')
      .single();

    if (kbError) {
      console.error('❌ Error creating knowledge base:', kbError);
      return null;
    }

    console.log('✅ Knowledge base created with ID:', kb.id);

    // 2. Asociar a la ejecución
    const { error: assocError } = await supabase
      .from('workpill_execution_knowledge_bases')
      .insert({
        workpill_execution_id: params.executionId,
        knowledge_base_id: kb.id
      });

    if (assocError) {
      console.error('❌ Error associating KB with execution:', assocError);
      return null;
    }

    console.log('✅ KB successfully associated with execution');
    return kb.id;
  } catch (error) {
    console.error('❌ Error in createKnowledgeBaseForExecution:', error);
    return null;
  }
}
